import { forceRefreshAccessToken, isStoredAccessTokenExpired, refreshAccessToken } from "@istari-frontend/auth";
import { extractSdkErrorDetail, findTransferError, isTransferCancelled } from "@istari-frontend/shared";

/** Renews the access token when storage says it has already expired. */
export async function refreshExpiredAccessToken(): Promise<void> {
  if (isStoredAccessTokenExpired()) {
    await refreshAccessToken();
  }
}

/**
 * Applies `prop` to a client constructed at call time, so the request carries the
 * token currently in storage rather than the one captured when the proxy was built.
 */
export async function invokeWithCurrentToken<TClient extends object>(
  createClient: (onToken?: (token: string | undefined) => void) => TClient,
  prop: keyof TClient,
  args: unknown[],
): Promise<unknown> {
  let requestToken: string | undefined;
  const client = createClient((token) => {
    requestToken = token;
  });
  const method = client[prop] as (...methodArgs: unknown[]) => unknown;
  try {
    return await method.apply(client, args);
  } catch (error) {
    if (requestToken && (await isOrdinaryAuthRejection(error))) {
      // A rejected write may already have committed. Renewal only helps the next explicit attempt.
      await forceRefreshAccessToken(requestToken).catch(() => undefined);
    }
    throw await withResponseDetail(error);
  }
}

async function isOrdinaryAuthRejection(error: unknown): Promise<boolean> {
  if (findTransferError(error) || isTransferCancelled(error) || !(error instanceof Error) || !hasResponse(error)) {
    return false;
  }
  if (error.response.status === 401) return true;
  if (error.response.status !== 400) return false;
  try {
    const body = await error.response.clone().text();
    if (body.trim() === "Invalid Token") return true;
    const parsed: unknown = JSON.parse(body);
    return typeof parsed === "object" && parsed !== null && "detail" in parsed && parsed.detail === "Invalid Token";
  } catch {
    return false;
  }
}

/**
 * The registry explains every failure in the response body's `detail`, and the SDK's error
 * middleware reports only the status line — which is the text the UI ends up showing. Restates the
 * failure with the explanation, carrying the response along for the callers that match on its
 * status and the original as the cause.
 */
async function withResponseDetail(error: unknown): Promise<unknown> {
  if (findTransferError(error) || !(error instanceof Error) || !hasResponse(error)) return error;
  const detail = await extractSdkErrorDetail(error).catch(() => undefined);
  if (!detail) return error;
  return Object.assign(new Error(detail, { cause: error }), { response: error.response });
}

function hasResponse(error: Error): error is Error & { response: Response } {
  return "response" in error && error.response instanceof Response;
}
